/**
 * `/internal/notifications/reach` + `/internal/notifications/quiet-orgs` wire
 * contracts — the operator read surface over notification POLL ACTIVITY
 * (#1947, storage in platform `0034`).
 *
 * ── THE ONE CLAIM THESE NUMBERS SUPPORT ────────────────────────────────────
 * platform-api records, per (org, member), the instant that member's client
 * last CALLED `GET /v1/notifications`. Everything below is derived from that
 * single fact, and it licenses exactly one statement:
 *
 *   "at least one member of this org called the notification endpoint after
 *    the announcement row was written" — i.e. HAD THE CHANCE to see it.
 *
 * It does NOT show that anyone saw it, opened it, or read it. It does not say
 * WHICH member polled, and it does not scale with headcount: a ten-person org
 * counts as one org the moment one person's client polls. Callers rendering
 * these fields must label them as a reachability LOWER BOUND, and must not use
 * the words read / delivered / received (or their Chinese equivalents) — that
 * prohibition is the direct lesson of #1945, where every surface said "sent"
 * about an announcement 583 of 592 orgs never received. A number that reads as
 * proof and is not is worse than no number.
 *
 * ── WHY "POLLED SINCE" IS A SOUND LOWER BOUND, AND NOT MERELY SUGGESTIVE ───
 * The client's cursor is `notifications.last_seen_at`, and it asks for rows
 * strictly newer than the newest it already holds. So if an org's newest poll
 * is later than an announcement row's `last_seen_at`, one of exactly two
 * things is true at that poll: the row was newer than the client's cursor and
 * came back in the response, or the cursor was already past it, which can only
 * happen if an earlier poll had already returned it. Either way the org's
 * client held the announcement. The converse is what makes the metric
 * discriminating rather than decorative: an org that polled BEFORE the
 * announcement was written and never since is NOT counted, because nothing it
 * ever fetched could have contained the announcement.
 *
 * ── AND WHY IT IS ONLY A LOWER BOUND ───────────────────────────────────────
 * Poll activity is throttled server-side, so a recorded instant can lag the
 * real poll by up to one throttle window — late, never early. An org whose
 * only poll fell inside that window since the announcement therefore reads as
 * un-polled until its next one. The number also grows over time on its own: a
 * client that was offline for a week and comes back moves its org into the
 * counted set retroactively, which is why this is computed on read rather than
 * frozen into the send record.
 *
 * snake_case, like every other `/internal/*` ops shape — ops-api relays these
 * bodies verbatim and ops-console reads them directly.
 */

/**
 * Per-announcement reachability, keyed by the fingerprint the announce route
 * returns (and stores as the `ops_audit` row's `target_id`).
 */
export interface NotificationReachItem {
  /** `v1:system:announce:<slug>` — echoed so a batch response can be joined. */
  readonly fingerprint: string;
  /**
   * Orgs that CURRENTLY hold a notification row for this fingerprint — the
   * denominator, and deliberately measured now rather than taken from the send
   * record's `orgs_targeted`.
   *
   * The two differ in one direction that matters: notification rows are swept
   * after `NOTIFICATION_RETENTION_DAYS`, and an org deleted since the send is
   * gone too. Reporting a live numerator against a frozen historical
   * denominator would drift the ratio downward for reasons that have nothing
   * to do with reach. Zero means the announcement is no longer measurable
   * here, NOT that it reached nobody — see `measurable`.
   */
  readonly orgs_holding: number;
  /**
   * Of those, how many have at least one member whose last recorded poll is
   * later than that org's copy of the announcement.
   *
   * Per-org comparison, not against a single global send time: re-firing a
   * slug rewrites each org's row and advances its `last_seen_at`, so the
   * question "could they have seen the CURRENT copy" has a different answer
   * per org and only the per-org form stays true after a correction.
   *
   * SYSTEMATICALLY LOW FOR THE FIRST `NOTIFICATION_POLL_THROTTLE_MS` after a
   * send, and not by a little: poll activity is recorded at most once per
   * throttle window per member, so a client that polls the instant an
   * announcement lands usually does not move its org's row until the window
   * has passed. An operator reading this column immediately after sending sees
   * a number that starts near zero and climbs — that is the metric working,
   * not the fleet failing, and any surface rendering it fresh has to say so.
   */
  readonly orgs_polled_since: number;
  /**
   * `false` when no notification row for this fingerprint survives, so the
   * ratio above is `0 / 0` and means nothing.
   *
   * A separate flag rather than a null count, because the console has to
   * render "we cannot tell" differently from "nobody polled" — collapsing them
   * is exactly the #1945 failure of stating as fact something the surface has
   * no way to know.
   *
   * WHAT IT DOES NOT SAY IS WHY. `orgs_holding` reaching zero has at least
   * four causes and this flag distinguishes none of them: retention swept the
   * rows after `NOTIFICATION_RETENTION_DAYS`; the fingerprint was never sent
   * at all (a hand-written URL, a mistyped slug); the send targeted zero orgs;
   * or every targeted org has since been deleted. A surface that renders this
   * as "the rows have aged out" has picked one of the four and asserted it —
   * the #1945 shape again, a confident statement the system has no way to
   * back. Name retention as an EXAMPLE if it helps the reader, never as the
   * diagnosis.
   */
  readonly measurable: boolean;
}

export interface NotificationReachResponse {
  readonly items: readonly NotificationReachItem[];
}

/**
 * One org with no member poll recorded inside the threshold.
 *
 * "Quiet", not "unreachable" and not "unread": all this says is that no
 * member's client has called the notification endpoint recently. A customer
 * who simply stopped using the desktop client is quiet and perfectly healthy;
 * so is one whose whole team is on holiday.
 */
export interface QuietNotificationOrg {
  readonly org_id: string;
  readonly org_name: string;
  /** Active members — the population that COULD have polled. */
  readonly member_count: number;
  /**
   * Newest poll across the org's members, or `null` when no member has ever
   * polled. Null is the more alarming of the two and the console distinguishes
   * them: an org that has never polled may never have installed the client at
   * all.
   */
  readonly last_polled_at: string | null;
}

export interface QuietNotificationOrgsResponse {
  readonly orgs: readonly QuietNotificationOrg[];
  /** Total matching orgs before `limit`/`offset`, for the pager. */
  readonly total: number;
  /** Echoed so the console labels the list with the threshold it asked for. */
  readonly threshold_days: number;
}

/** Default silence threshold when the caller names none. */
export const NOTIFICATION_QUIET_ORG_DEFAULT_DAYS = 14;
export const NOTIFICATION_QUIET_ORG_MIN_DAYS = 1;
export const NOTIFICATION_QUIET_ORG_MAX_DAYS = 365;

/**
 * Cap on one `/internal/notifications/reach` batch.
 *
 * The console asks for exactly the fingerprints on the history page it is
 * rendering, so the cap only has to exceed a page. It exists to keep a
 * hand-written URL from turning one operator request into an unbounded scan.
 */
export const NOTIFICATION_REACH_MAX_FINGERPRINTS = 50;
